import {
  GldBandEnum,
  IActivityTransition,
  IIlpServiceState,
  IlpService,
  IMessage,
  ITakeSubmitComplete,
  IUser,
  LanguageEnum,
  AssignmentStatusEnum,
  SectionTypeEnum,
  ServiceStatusEnum,
  TranslationService,
  IIlp
} from "@imaginelearning/mps-api-service";
import { ISegment, ITakeComplete, StudentContentReader } from "@imaginelearning/mps-shared-component-library";
import { UxAvatarEnum, UxPreloaderTypeEnum, loadSpeechstream } from "@imaginelearning/mps-ux-component-library";
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import useGradeRange from "hooks/usegraderange";
import ActivityCompleteToast from "../../components/edge-activity-complete-toast/edge-activity-complete-toast";
import {
  ActivityTransitionManager,
  TransitionActionEnum,
  initialActivityTransitionState,
  IActivityTransitionState
} from "../../components/common/activity-transition-manager";
import { IlpContext } from "../../context-providers/ilp.provider";
import { RewardContext } from "../../context-providers/reward.provider";
import { UserContext } from "../../context-providers/user.provider";
import getGradeband from "../../helpers/get-gradeband";
import EdgePlayerPreloader from "../edge-player-preloader/edge-player-preloader";
import EdgePlayerTokenTimer from "../edge-player-token-timer/edge-player-token-timer";
import { useOnDemandTutoring, OnDemandTutoringButton, ActivityTypeEnum } from "../../ondemandtutoring";

type TLessonLocationState = {
  lessonPathId: number;
  lessonPathVersion: number;
  lessonTitle: string;
  ilpSubject: string;
  isSpanishContent: boolean;
  calculatorType: string;
  gradeLevel: number;
  gldBand: GldBandEnum;
  alpId?: string;
};

function EdgeLaunch() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [translatedObjectState, setTranslatedObjectState] = useState([]);
  const { ilpId, getUpdatedIlp, setShowTokenExpiredAlert, ilpState, standardCode } = useContext(IlpContext);
  const { userState } = useContext(UserContext);
  const { is9to12 } = useGradeRange();
  const {
    lessonPathId,
    lessonPathVersion,
    lessonTitle,
    ilpSubject,
    isSpanishContent,
    calculatorType,
    gradeLevel,
    gldBand,
    alpId
  } = useLocation().state as TLessonLocationState;
  const [activityState, setActivityState] = useState({
    status: ServiceStatusEnum.LOADING,
    payload: {},
    showPlayer: false
  });
  const user: IUser = userState.response as IUser;

  useEffect(() => {
    loadSpeechstream();
  }, []);

  const { showTtsForSubject, showLanguageTranslation } = useMemo(() => {
    const tts = !!user.textToSpeech && user.textToSpeech.some((x) => x.toLowerCase() === ilpSubject.toLowerCase());
    const lang =
      !!user.languageTranslation && user.languageTranslation.some((x) => x.toLowerCase() === ilpSubject.toLowerCase());

    return { showTtsForSubject: tts, showLanguageTranslation: lang };
  }, [ilpSubject, user.languageTranslation, user.textToSpeech]);

  const appEnv = useMemo(() => {
    const env = (window.STUDENT_ENV?.REACT_APP_ENV ?? "").toLowerCase();
    if (env === "local") return "dev";
    if (env === "prod") return "production";
    return env;
  }, []);

  let ilpLanguage = "";
  if (showLanguageTranslation) {
    ilpLanguage = (ilpState.response as IIlp).translationLanguage;
  }
  if (!ilpLanguage && isSpanishContent) {
    ilpLanguage = "es";
  }

  const [selectedLanguage, setSelectedLanguage] = useState(ilpLanguage || "en");
  const [refreshToken, setRefreshToken] = useState(Date.now().toString());
  const [currentSegmentId, setCurrentSegmentId] = useState("");
  const [stateChangeCount, setStateChangeCount] = useState(0);
  const { getReward } = useContext(RewardContext);

  const browserTitle = document.querySelectorAll("head title")[0];
  const titleText = `${lessonTitle} | ${process.env.REACT_APP_WEBSITE_NAME}`;
  if (browserTitle) {
    browserTitle.innerHTML = titleText;
  }

  useEffect(() => {
    sessionStorage.setItem("userID", user.id);
    sessionStorage.setItem("gradeLevel", user.gradeLevel?.toString());
  }, [user]);

  const [activityTransitionModalState, setActivityTransitionModalState] =
    useState<IActivityTransitionState>(initialActivityTransitionState);

  const initialActivityTransitionToastState = {
    show: false,
    starsEarned: 0,
    transitionMessage: null as IMessage[] | string | null,
    activityStatus: AssignmentStatusEnum.PASSED
  };

  const [activityTransitionToastState, setActivityTransitionToastState] = useState(initialActivityTransitionToastState);
  const [totalScore, setTotalScore] = useState<number | null>(0);
  const [takeTotal, setTakeTotal] = useState(0);

  const language = isSpanishContent ? LanguageEnum.Spanish : LanguageEnum.English;
  const gradeband = getGradeband(user.gradeLevel);

  const handleActivityTransitionToastClose = () => {
    setActivityTransitionToastState({ ...initialActivityTransitionToastState });
  };

  const redirectToLessons = useCallback(() => {
    getReward(Date.now().toString());
    if (ilpId) {
      getUpdatedIlp(Date.now().toString());
    }
    navigate(-1);
  }, [getReward, ilpId, navigate, getUpdatedIlp]);

  const handleReaderClose = () => {
    redirectToLessons();
  };

  const handleReaderSegmentReady = (segment: any) => {
    // Make onDemandTutoring Visible if the segment has questions and is right ActivityType
    const segmentHasQuestions = segment.components.find(
      (item: any) => item.type === "Question" || item.type === "QuestionItem"
    );

    let { activityType } = activityState.payload as any;
    activityType = activityType.trim();
    onDemandTutoring.setVisible(
      !!segmentHasQuestions &&
      user?.gradeLevel > 2 &&
      ilpSubject === "Math" &&
      (activityType === ActivityTypeEnum.SUPPORTED_PRACTICE ||
        activityType === ActivityTypeEnum.ASSIGNMENT ||
        activityType === ActivityTypeEnum.INSTRUCTION)
    );

    setSelectedLanguage(selectedLanguage);
    TranslationService.translation(
      isSpanishContent ? "es" : "en",
      selectedLanguage || "en",
      setTranslatedObjectState,
      translatedObjectState
    );
  };

  const displayActivityTransitionModalForModifiedLesson = (message: string | null = null) => {
    setActivityTransitionModalState((prevState) => ({
      ...prevState,
      show: true,
      transitionMessage: message ?? t("YourTeacherWantsYouToWorkOnSomethingElse"),
      action: TransitionActionEnum.GO_BACK,
      onClose: (action: TransitionActionEnum, isClosedUsingX?: boolean) => {
        redirectToLessons();
      }
    }));
  };

  const getToastMessage = (transitionMessage: string | { message?: string }[] | undefined): string => {
    if (typeof transitionMessage === "string") {
      return transitionMessage;
    }
    if (Array.isArray(transitionMessage) && transitionMessage.length > 0) {
      return transitionMessage[0]?.message ?? "";
    }
    return "";
  };

  useEffect(() => {
    if (!ilpId && !alpId) {
      navigate("/");
    }
  }, [ilpId, alpId, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      const activityTokenResult = await IlpService.getActivityToken(
        alpId ?? ilpId,
        lessonPathId,
        lessonPathVersion,
        language,
        gradeband,
        standardCode,
        gradeLevel,
        gldBand
      );

      if (activityTokenResult.status === ServiceStatusEnum.LOADED) {
        if (activityTokenResult.payload && !activityTokenResult.payload.token) {
          const messageType = activityTokenResult.payload?.messages[0]?.message;
          if (messageType === "EducatorModification" || messageType === "MasteryCheckQuestionError") {
            const { status, payload } = activityTokenResult;
            setActivityState({ status, payload, showPlayer: false });
            displayActivityTransitionModalForModifiedLesson(
              messageType === "EducatorModification" ? null : t("WeCantStartThisActivity")
            );
          } else {
            redirectToLessons();
          }
        }

        if (activityTokenResult.payload && activityTokenResult.payload.token) {
          const messages = activityTokenResult.payload.messages as IMessage[];
          let showPlayer = true;
          if (activityTokenResult.payload.isMasteryCheck) {
            showPlayer = false;
            setActivityTransitionModalState((prevState) => ({
              ...prevState,
              show: true,
              transitionMessage: messages,
              activityStatus: AssignmentStatusEnum.NOT_STARTED,
              action: TransitionActionEnum.CLOSE,
              onClose: (action: TransitionActionEnum, isClosedUsingX?: boolean) => {
                if (isClosedUsingX) {
                  redirectToLessons();
                } else {
                  setActivityState((prevState) => ({ ...prevState, showPlayer: true }));
                }
              }
            }));
          }

          const { status, payload } = activityTokenResult;
          setActivityState({ status, payload, showPlayer });
        }
      }

      if (activityTokenResult.status === ServiceStatusEnum.ERROR) {
        redirectToLessons();
      }
    };

    if ((ilpId || alpId) && refreshToken) {
      setActivityState({ status: ServiceStatusEnum.LOADING, payload: {}, showPlayer: false });
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    redirectToLessons,
    ilpId,
    alpId,
    lessonPathId,
    lessonPathVersion,
    ilpSubject,
    refreshToken,
    language,
    gradeband,
    standardCode
  ]);

  useLayoutEffect(() => {
    if (isSpanishContent) i18n.changeLanguage("es");
    return () => {
      if (isSpanishContent) i18n.changeLanguage("en");
    };
  }, [isSpanishContent, i18n]);

  const onDemandTutoring = useOnDemandTutoring();

  const handleActivityComplete = (isLessonCompleted: boolean) => {
    handleActivityTransitionToastClose();
    if (isLessonCompleted) {
      redirectToLessons();
    } else {
      setRefreshToken(Date.now().toString());
    }
  };

  const handleReaderSegmentStateChange = (segment: ISegment) => {
    // To limit unnecessary traffic, only hit the API for the first on a new segment then (arbitrarily)
    // every fifth after to keep the check for teacher modifications going every ~10 seconds during videos
    if (stateChangeCount % 5 === 0 || segment.segmentId !== currentSegmentId) {
      IlpService.setSegmentStateChange({
        segmentID: segment.segmentId,
        takeID: activityState.payload && (activityState.payload as any).token.takeID
      }).then((result: IIlpServiceState<IActivityTransition>) => {
        if (result.status === ServiceStatusEnum.LOADED) {
          const response = result.response as IActivityTransition;
          const lessonModifiedByTeacher = !response.isLessonAssigned || response.isLessonCompletedByTeacher || response.isAssignmentInvalidated;
          if (lessonModifiedByTeacher) {
            displayActivityTransitionModalForModifiedLesson();
          }
        }
      });
    }

    // Make onDemandTutoring Visible if the segment has questions and is right ActivityType
    // Check if ShowNudge for OnDemand should be shown (if tried twice)
    // if no questions in Segment, vivible should be false (hidden)
    const question = segment.components.find(
      (item: any) =>
        (item.type === "Question" || item.type === "QuestionItem") &&
        item.state.attempts > 0 &&
        item.state.correct === false
    );
    if (question) {
      const noOfAttempts = question?.state?.attempts ?? 0;
      if (noOfAttempts > 0) {
        onDemandTutoring.setEnabled(true);
        onDemandTutoring.setAvailable(true);
      }
      onDemandTutoring.setShowNudge(noOfAttempts >= 1);
    } else {
      onDemandTutoring.setAvailable(false);
      onDemandTutoring.setEnabled(false);
      onDemandTutoring.setShowNudge(false);
    }

    if (segment.segmentId !== currentSegmentId) {
      setCurrentSegmentId(segment.segmentId);
      setStateChangeCount(0);
    }
    setStateChangeCount(stateChangeCount + 1);
  };

  const handleReaderTakeSubmitComplete = (take: ITakeComplete) => {
    const takeComplete: ITakeSubmitComplete = {
      totalScore: take.totalScore,
      takeId: (activityState.payload as any).token.takeID,
      gradeband
    };
    setTotalScore(take.totalScore);
    setTakeTotal(take.totalPossibleScore || 0);

    // Update modal state with scores
    setActivityTransitionModalState((prevState) => ({
      ...prevState,
      totalScore: take.totalScore,
      takeTotal: take.totalPossibleScore || 0
    }));

    IlpService.setTakeSubmitComplete(takeComplete, user.schoolYear).then(
      (result: IIlpServiceState<IActivityTransition>) => {
        if (result.status === ServiceStatusEnum.LOADED) {
          const response = result.response as IActivityTransition;
          const lessonModifiedByTeacher = !response.isLessonAssigned || response.isLessonCompletedByTeacher;
          if (lessonModifiedByTeacher) {
            displayActivityTransitionModalForModifiedLesson();
          } else if (response.points || is9to12) {
            const currentActivityStatus: AssignmentStatusEnum = response.isPassed
              ? AssignmentStatusEnum.PASSED
              : AssignmentStatusEnum.NOT_PASSED;

            const messages = response.messages as IMessage[];
            if (messages && messages[0].type && messages[0].type === 2) {
              setActivityTransitionToastState((prevState) => ({
                ...prevState,
                show: true,
                starsEarned: response.points,
                transitionMessage: t(messages[0].translationKey as any, messages[0].message)
              }));
              setTimeout(() => {
                handleActivityComplete(response.isLessonCompleted);
              }, 2000);
            } else {
              setActivityTransitionModalState((prevState) => ({
                ...prevState,
                show: true,
                transitionMessage: messages,
                activityStatus: currentActivityStatus,
                starsEarned: response.points,
                action:
                  response.isPassed || response.isLessonCompleted
                    ? TransitionActionEnum.GO_BACK
                    : TransitionActionEnum.GET_NEXT_TOKEN,
                isLessonCompleted: response.isLessonCompleted,
                displayScore: true,
                onClose: (action: TransitionActionEnum, isClosedUsingX?: boolean) => {
                  if (isClosedUsingX ?? action === TransitionActionEnum.CLOSE) {
                    setActivityState((prevState) => ({ ...prevState, showPlayer: true }));
                  } else if (action === TransitionActionEnum.GO_BACK) {
                    redirectToLessons();
                  } else if (action === TransitionActionEnum.GET_NEXT_TOKEN) {
                    setRefreshToken(Date.now().toString());
                  }
                }
              }));
            }
          }
        } else if (result.status === ServiceStatusEnum.ERROR) {
          displayActivityTransitionModalForModifiedLesson();
        }
      }
    );
  };

  const handleTokenExpired = () => {
    setShowTokenExpiredAlert(true);
    redirectToLessons();
  };

  const handleReaderLanguageChange = (languageOption: any) => {
    const languageCode = languageOption.googleAbbr;
    IlpService.updateIlpLanguage(ilpId, languageCode);
    TranslationService.translation(
      window.self.document.getElementsByTagName("html")[0].lang || (isSpanishContent ? "es" : "en"),
      languageCode,
      setTranslatedObjectState,
      translatedObjectState
    );
    setSelectedLanguage(languageCode);
  };

  const handlePanelChange = () => {
    if ((!isSpanishContent && selectedLanguage !== "en") || (isSpanishContent && selectedLanguage !== "es")) {
      TranslationService.translation(
        isSpanishContent ? "es" : "en",
        selectedLanguage,
        setTranslatedObjectState,
        translatedObjectState
      );
    }
  };

  return (
    <>
      {activityState.status === ServiceStatusEnum.LOADING && (
        <EdgePlayerPreloader loaderType={UxPreloaderTypeEnum.THREE_DOTS} />
      )}
      {activityState.status === ServiceStatusEnum.LOADED && activityState.payload && (
        <>
          <ActivityTransitionManager
            state={activityTransitionModalState}
            avatar={user.selectedAvatar as UxAvatarEnum}
            onStateChange={setActivityTransitionModalState}
          />
          {activityState.showPlayer && (
            <>
              <StudentContentReader
                token={(activityState.payload as any).token.token}
                lessonTitle={lessonTitle}
                activityType={(activityState.payload as any).activityType}
                doolittleBaseMediaUrl={window.STUDENT_ENV?.REACT_APP_DOOLITTLE_READER_BASE_URL}
                doolittleBaseApiUrl={window.STUDENT_ENV?.REACT_APP_DOOLITTLE_API_BASE_URL}
                doolittleVersion={window.STUDENT_ENV?.REACT_APP_DOOLITTLE_READER_VERSION}
                debug={window.STUDENT_ENV?.REACT_APP_DOOLITTLE_READER_LAUNCH_OPTIONS_DEBUG === "true"}
                disableFastForward={
                  window.STUDENT_ENV?.REACT_APP_DOOLITTLE_READER_LAUNCH_OPTIONS_DISABLEFASTFORWARD === "true" ||
                  sessionStorage.getItem("FastForwardPlayer") === null
                }
                isMasteryCheck={(activityState.payload as any).isMasteryCheck}
                theme={gradeband}
                showTextToSpeech={showTtsForSubject}
                previewMode={false}
                showLanguageTranslation={showLanguageTranslation}
                selectedLanguage={selectedLanguage}
                closeCallback={handleReaderClose}
                segmentStateChangeCallback={handleReaderSegmentStateChange}
                takeSubmitCompleteCallback={handleReaderTakeSubmitComplete}
                segmentReadyCallback={handleReaderSegmentReady}
                languageChangeCallback={handleReaderLanguageChange}
                panelChangeCallback={handlePanelChange}
                avatar={user.selectedAvatar as UxAvatarEnum}
                isSpanishContent={isSpanishContent}
                calculatorType={calculatorType}
                disableAnnotations={!(activityState.payload as any).isAnnotationOn}
                isAudioCompletionRequired={true}
                appEnv={appEnv}
                itemEngineAuthorUri={window.STUDENT_ENV?.REACT_APP_ITEM_ENGINE_AUTHOR_URI}
                itemEngineItemsUri={window.STUDENT_ENV?.REACT_APP_ITEM_ENGINE_ITEMS_URI}
                footerAdditionalActions={[
                  <OnDemandTutoringButton
                    key={1}
                    user={{
                      userId: user.username,
                      name: `${user.firstname} ${user.lastname}`,
                      firstName: user.firstname,
                      lastName: user.lastname,
                      grade: user.gradeLevel === 0 ? "k" : user.gradeLevel.toString(),
                      schoolId: user.organization?.orgId ?? "unavailable",
                      schoolName: user.organization?.name,
                      districtId: user.organization?.parentOrganization?.orgId ?? "unavailable",
                      districtName: user.organization?.parentOrganization?.name ?? "unavailable",
                      subject: "Math",
                      lessonName: lessonTitle
                    }}
                  />
                ]}
              />

              <EdgePlayerTokenTimer
                issuedAt={(activityState.payload as any).token.issuedAt}
                expiresAt={(activityState.payload as any).token.expiresAt}
                handleTokenExpired={handleTokenExpired}
              />
            </>
          )}
        </>
      )}
      {activityTransitionToastState.show && (
        <div className="toast-top-corner">
          <ActivityCompleteToast
            show={activityTransitionToastState.show}
            message={getToastMessage(activityTransitionToastState.transitionMessage)}
            starsEarned={activityTransitionToastState.starsEarned}
            handleClose={handleActivityTransitionToastClose}
          />
        </div>
      )}
    </>
  );
}

export default EdgeLaunch;
